import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageIcon } from '@heroicons/react/24/outline';

import { apiPatch } from '../api/client';
import type { paths } from '../api/types.gen';
import { useAuth } from '../auth/AuthContext';
import { cn } from '../ui';
import { SUPPORTED_LOCALES, type Locale } from '../i18n';

type PreferencesBody =
	paths['/users/me/preferences']['patch']['requestBody']['content']['application/json'];
type PreferencesResponse =
	paths['/users/me/preferences']['patch']['responses'][200]['content']['application/json'];

const LOCALE_LABELS: Record<Locale, string> = {
	en: 'English',
	ar: 'العربية',
};

const LOCALE_SHORT: Record<Locale, string> = {
	en: 'EN',
	ar: 'AR',
};

/**
 * Two-state language toggle next to the bell. Click cycles EN ↔ AR.
 *
 * Persistence (in priority order):
 *   1. localStorage — handled by i18next-browser-languagedetector, instant
 *      on next visit on the same device.
 *   2. Backend `preferred_locale` on the Users row — fire-and-forget PATCH
 *      so the choice follows the user across devices. If the BE endpoint
 *      isn't reachable (offline, not logged in, endpoint not yet shipped),
 *      we silently swallow — localStorage still keeps it sticky.
 *
 * The switcher is intentionally a button (not a dropdown) since we only
 * support two locales today. When we add a third, swap to a Headless UI
 * Menu without changing the calling sites in MainLayout.
 */
export function LanguageSwitcher() {
	// `defaultNS: 'common'` is set in i18n init, so no explicit namespace
	// arg is needed (and react-i18next 15.x strict types reject it without
	// a global Resources declaration, which we'll add in Phase 2 once the
	// JSON files have stabilized).
	const { i18n, t } = useTranslation();
	const { user, status } = useAuth();
	const current = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2) as Locale;
	const next: Locale = current === 'en' ? 'ar' : 'en';

	const onToggle = async () => {
		await i18n.changeLanguage(next);
		// Sync to backend so the preference follows the user across devices.
		// Fire-and-forget: a network error here is non-blocking, the local
		// state already changed via localStorage.
		if (status === 'authenticated' && user) {
			const body: PreferencesBody = { preferred_locale: next };
			apiPatch<PreferencesResponse, PreferencesBody>(
				'/users/me/preferences',
				body
			).catch(() => {
				/* swallow — localStorage carries the day */
			});
		}
	};

	return (
		<button
			type="button"
			onClick={onToggle}
			aria-label={t('language.switchTo', { lang: LOCALE_LABELS[next] })}
			title={t('language.switchTo', { lang: LOCALE_LABELS[next] })}
			className={cn(
				'inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md',
				'text-sm font-medium text-ink-soft hover:text-ink hover:bg-slate-100',
				'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500'
			)}
		>
			<LanguageIcon className="h-4 w-4" aria-hidden />
			<span className="text-xs">{LOCALE_SHORT[current]}</span>
		</button>
	);
}

// Re-exported so callers don't need to import from i18n module just to know
// the supported set — keeps the import surface tiny.
export { SUPPORTED_LOCALES };
